import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, of, throwError } from 'rxjs';
import { finalize, shareReplay, switchMap, take } from 'rxjs/operators';

import { SessionExpiredDialogComponent, SessionExpiredDialogData } from './session-expired-dialog.component';

/**
 * Auth0-Fehlercodes, bei denen sich kein Token mehr still erneuern laesst —
 * der Refresh-Token ist abgelaufen (Maximum Refresh Token Lifetime, 30 Tage)
 * oder fehlt. Nur eine neue Anmeldung hilft.
 */
const SESSION_ENDED_CODES = [
  'invalid_grant',
  'missing_refresh_token',
  'login_required',
  'consent_required',
  'interaction_required',
];

export function isSessionEnded(error: any): boolean {
  return SESSION_ENDED_CODES.includes(error?.error);
}

/**
 * Request wurde nicht gesendet: Sitzung abgelaufen und der Anwender hat die
 * erneute Anmeldung abgebrochen. Kein Abmelden — die Eingaben auf der Seite
 * sollen erhalten bleiben (DOCU-1323).
 */
export class SessionExpiredError extends HttpErrorResponse {
  constructor(url?: string) {
    super({
      status: 401,
      statusText: 'Session expired',
      url,
      error: { langKey: 'ERROR.SESSION_EXPIRED_UNSAVED' },
    });
  }
}

@Injectable({ providedIn: 'root' })
export class SessionService {
  private reauthentication$: Observable<string> | null = null;

  constructor(private dialog: MatDialog, private auth: AuthService) {}

  /**
   * Fragt den Anwender nach einer erneuten Anmeldung und liefert danach einen
   * frischen Access-Token. Laufen mehrere Requests gleichzeitig auf die
   * abgelaufene Sitzung, teilen sie sich einen Dialog.
   */
  reauthenticate(): Observable<string> {
    if (!this.reauthentication$) {
      this.reauthentication$ = this.auth.user$.pipe(
        take(1),
        switchMap(user => {
          const data: SessionExpiredDialogData = { email: user?.email, sub: user?.sub };
          return this.dialog
            .open<SessionExpiredDialogComponent, SessionExpiredDialogData, string>(
              SessionExpiredDialogComponent,
              { data, disableClose: true, width: '440px' }
            )
            .afterClosed();
        }),
        switchMap(token => (token ? of(token) : throwError(() => new SessionExpiredError()))),
        finalize(() => (this.reauthentication$ = null)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.reauthentication$;
  }
}
